/**
 * MarkAssetAsSoldModal Component
 *
 * Modal for marking an asset as sold/disposed.
 * Closes the asset, excludes it from net worth, and zeroes current value.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Account, CloseAccountRequest } from '../../types/account.types';
import { X, Info } from 'lucide-react';

export interface MarkAssetAsSoldPayload extends CloseAccountRequest {
  salePrice?: number;
  notesAppend?: string;
}

interface MarkAssetAsSoldModalProps {
  account: Account;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: MarkAssetAsSoldPayload) => void;
  loading?: boolean;
}

export function MarkAssetAsSoldModal({
  account,
  isOpen,
  onClose,
  onConfirm,
  loading = false
}: MarkAssetAsSoldModalProps): React.ReactElement | null {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const today = new Date().toISOString().split('T')[0];

  const [saleDate, setSaleDate] = useState(today);
  const [salePrice, setSalePrice] = useState<string>('');
  const [reason, setReason] = useState('Sold');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;

    setSaleDate(today);
    setSalePrice('');
    setReason('Sold');
    setError('');

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, today]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!saleDate) {
      setError('Sale date is required');
      return;
    }

    const saleDateObj = new Date(saleDate);
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    if (saleDateObj > now) {
      setError('Sale date cannot be in the future');
      return;
    }

    const parsedPrice = salePrice === '' ? undefined : Number(salePrice);
    if (parsedPrice !== undefined && (Number.isNaN(parsedPrice) || parsedPrice < 0)) {
      setError('Sale price must be a valid non-negative number');
      return;
    }

    const notesParts: string[] = [];
    if (parsedPrice !== undefined) {
      notesParts.push(`Sale price: ${parsedPrice}`);
    }
    if (reason && reason !== 'Sold') {
      notesParts.push(reason);
    }

    onConfirm({
      closureDate: saleDate,
      closureReason: reason || 'Sold',
      includeInNetWorth: false,
      currentValue: 0,
      salePrice: parsedPrice,
      notesAppend: notesParts.length > 0 ? notesParts.join('. ') : undefined
    });
  };

  return (
    <div
      className="fixed inset-0 z-[60] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mark-asset-sold-title"
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900"
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div>
              <h2 id="mark-asset-sold-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Mark as Sold
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {account.name}
              </p>
            </div>
            <button
              ref={firstFocusableRef}
              type="button"
              onClick={onClose}
              className="btn-ghost btn-sm rounded-md p-2"
              aria-label="Close modal"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-800 dark:text-blue-200">
              <Info className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
              <p>
                This keeps a historical record of the asset but closes it, sets current value to $0,
                and excludes it from net worth. Prefer this over deleting unless you want the record gone permanently.
              </p>
            </div>

            <div>
              <label htmlFor="saleDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sale / disposal date *
              </label>
              <input
                type="date"
                id="saleDate"
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
                max={today}
                required
                className="input w-full"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="salePrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sale price (optional)
              </label>
              <input
                type="number"
                id="salePrice"
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                min="0"
                step="0.01"
                placeholder="For your records only"
                className="input w-full"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="saleReason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reason (optional)
              </label>
              <input
                type="text"
                id="saleReason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                className="input w-full"
                disabled={loading}
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary btn-md"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary btn-md"
                disabled={loading}
              >
                {loading ? 'Saving…' : 'Mark as Sold'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
